// People: pools, hire/fire orders, NRE dial, allocation — and the M3 talent
// market: morale, comp policy, labor heat, and the named-star roster (§9).

import type { Discipline, TickReport } from "../../engine/types.js";
import { DISCIPLINES } from "../../engine/types.js";
import { interpolate } from "../../engine/calendar.js";
import type { ScreenProps } from "./shared.js";
import { money, monthLabel, pct } from "../format.js";
import { useNarrow } from "../media.js";
import { Bar, MultiLineChart, Stepper } from "../components/widgets.js";

export function People({ snap, content, session }: ScreenProps) {
  const { view, pending } = snap;
  const narrow = useNarrow();
  const eco = content.economy;
  const m = view.monthIndex;
  const td = eco.talent;
  const heat = interpolate(td.heat, m);
  const morale = view.talent.morale;
  const compQueued = pending.find((c) => c.type === "setCompPolicy");
  const compShown = compQueued && compQueued.type === "setCompPolicy" ? compQueued.policy : view.talent.compPolicy;

  const salary = (d: Discipline) =>
    interpolate(
      eco.salaries.map((p) => ({ m: p.m, v: p[d] })),
      m,
    );

  const pendingCount = (type: "hire" | "fire", d: Discipline) => {
    const c = pending.find((x) => x.type === type && x.discipline === d);
    return c && c.type === type ? c.count : 0;
  };

  const queueDelta = (type: "hire" | "fire", d: Discipline, count: number) => {
    if (count <= 0) {
      const idx = pending.findIndex((x) => x.type === type && x.discipline === d);
      if (idx >= 0) session.unqueue(idx);
      return;
    }
    session.queue({ type, discipline: d, count: Math.min(50, count) });
  };

  const nreQueued = pending.find((c) => c.type === "setNre");
  const nreShown = nreQueued && nreQueued.type === "setNre" ? nreQueued.engineers : view.nreEngineers;
  const nreRate = interpolate(eco.nre.billRatePerEngMonth, m);

  return (
    <div>
      <h2>PEOPLE</h2>

      <div className="row">
        <div className="tile">
          <div className="k">MORALE</div>
          <div className={`v${morale < 40 ? " bad" : morale < 55 ? " warn" : ""}`}>{morale.toFixed(0)}/100</div>
          <div className="sub">
            attrition ×
            {Math.min(td.moraleAttritionHi, Math.max(td.moraleAttritionLo, 1 + (td.moraleBaseline - morale) * td.moraleAttritionCoef)).toFixed(2)} · drivers:
            comp, layoffs, the stock
          </div>
        </div>
        <div className="tile">
          <div className="k">LABOR MARKET HEAT</div>
          <div className={`v${heat > 0.75 ? " warn" : ""}`}>{pct(heat, 0)}</div>
          <div className="sub">
            recruiting ×{(1 + td.heatRecruitCoef * heat).toFixed(2)} · attrition ×{(1 + td.heatAttritionCoef * heat).toFixed(2)}
          </div>
        </div>
        <div className="tile">
          <div className="k">COMP POLICY</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {([-1, 0, 1] as const).map((p) => (
              <button
                key={p}
                className={`act${compShown === p ? " warn" : ""}`}
                onClick={() => session.queue({ type: "setCompPolicy", policy: p })}
              >
                {p < 0 ? "BELOW" : p > 0 ? "ABOVE" : "MARKET"}
              </button>
            ))}
          </div>
          <div className="sub">
            payroll ×{(1 + td.compPayrollSpan * compShown).toFixed(2)} · morale baseline {compShown > 0 ? "+" : ""}
            {(td.compMoraleSpan * compShown).toFixed(0)}
          </div>
        </div>
      </div>

      {view.talent.integrationMonthsLeft > 0 && (
        <div className="panel">
          <span className="warn-text">
            ◆ ACQUISITION INTEGRATION: {view.talent.integrationMonthsLeft} months of elevated churn (attrition ×
            {td.integrationAttritionMult.toFixed(1)}) while the teams merge.
          </span>
        </div>
      )}

      {view.talent.stars.length > 0 && (
        <div className="panel">
          <div className="title">NAMED STARS — outsized hands, outsized packages</div>
          {view.talent.stars.map((star) => (
            <div key={star.id} className="bar-label">
              <span>
                ★ {star.name} <span className="dim">({star.discipline}, since {monthLabel(star.joinedMonth)})</span>
              </span>
              <span>
                worth ~{star.effHeads} engineers · paid {star.salaryMult.toFixed(1)}× band
              </span>
            </div>
          ))}
          <span className="hint">Rivals hunt names first. Counters cost retention money; losses cost the whole room's morale.</span>
        </div>
      )}

      <div className="panel">
        <div className="title">HEADCOUNT BY DISCIPLINE — TRAILING {Math.min(48, view.kpiHistory.length)}MO</div>
        <MultiLineChart
          width={420}
          height={80}
          series={[
            { label: "design", color: "var(--ink)", data: view.kpiHistory.slice(-48).map((k) => k.headcountByDiscipline.design) },
            { label: "verification", color: "var(--market-1)", data: view.kpiHistory.slice(-48).map((k) => k.headcountByDiscipline.verification) },
            { label: "implementation", color: "var(--market-4)", data: view.kpiHistory.slice(-48).map((k) => k.headcountByDiscipline.implementation) },
          ]}
        />
      </div>

      <div className="panel">
        <div className="title">ENGINEERING ORGANIZATION — hires arrive next month, ramp {eco.hireRampMonths}mo at {(eco.rampEffectiveness * 100).toFixed(0)}%</div>
        {narrow ? (
          // phones: one block per discipline — steppers can't share a table row with 5 number columns
          <div>
            {DISCIPLINES.map((d) => {
              const pool = view.people.pools[d];
              const ramping = pool.ramping.reduce((a, r) => a + r.count, 0);
              const loaded = (salary(d) / 12) * eco.overheadBurden;
              return (
                <div key={d} style={{ padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
                  <div className="bar-label">
                    <span>{d.toUpperCase()}</span>
                    <span>
                      {pool.productive} productive{ramping > 0 ? ` +${ramping} ramping` : ""} · {money(salary(d))}/yr · {money(loaded)}/mo loaded
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", marginTop: 4 }}>
                    <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                      <span className="dim">HIRE</span>
                      <Stepper value={0} queued={pendingCount("hire", d) || undefined} max={50} onChange={(v) => queueDelta("hire", d, v)} />
                    </span>
                    <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                      <span className="dim">LAY OFF</span>
                      <Stepper value={0} queued={pendingCount("fire", d) || undefined} max={pool.productive + ramping} onChange={(v) => queueDelta("fire", d, v)} />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="scroll-x">
            <table>
              <thead>
                <tr>
                  <th>discipline</th>
                  <th className="r">productive</th>
                  <th className="r">ramping</th>
                  <th className="r">salary</th>
                  <th className="r">loaded/mo</th>
                  <th className="r">HIRE</th>
                  <th className="r">LAY OFF</th>
                </tr>
              </thead>
              <tbody>
                {DISCIPLINES.map((d) => {
                  const pool = view.people.pools[d];
                  const ramping = pool.ramping.reduce((a, r) => a + r.count, 0);
                  const loaded = (salary(d) / 12) * eco.overheadBurden;
                  return (
                    <tr key={d}>
                      <td>{d}</td>
                      <td className="r">{pool.productive}</td>
                      <td className="r">{ramping > 0 ? `+${ramping}` : "—"}</td>
                      <td className="r">{money(salary(d))}/yr</td>
                      <td className="r">{money(loaded)}</td>
                      <td className="r">
                        <Stepper value={0} queued={pendingCount("hire", d) || undefined} max={50} onChange={(v) => queueDelta("hire", d, v)} />
                      </td>
                      <td className="r">
                        <Stepper value={0} queued={pendingCount("fire", d) || undefined} max={pool.productive + ramping} onChange={(v) => queueDelta("fire", d, v)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <span className="hint">
          Recruiting costs {(eco.recruitingFeePctAnnual * 100).toFixed(0)}% of first-year salary; severance {eco.severanceMonths} months
          loaded. Attrition runs {(eco.attritionMonthly * 100).toFixed(1)}%/mo.
        </span>
      </div>

      <div className="panel">
        <div className="title">NRE / DESIGN-SERVICES CONSULTING — the Stage-1 bridge</div>
        <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
          <Stepper value={view.nreEngineers} queued={nreQueued && nreQueued.type === "setNre" ? nreShown : undefined} max={eco.nre.maxEngineers} bigStep={2} onChange={(v) => session.queue({ type: "setNre", engineers: v })} />
          <span>
            designers billed to customers at {money(nreRate)}/eng-month ≈ <b>{money(nreShown * nreRate)}/mo</b>
          </span>
        </div>
        <span className="hint">
          Cash now, but those designers aren't building YOUR chip. Customer demand caps at {eco.nre.maxEngineers} engineers.
        </span>
      </div>

      {snap.report && (
        <div className="panel">
          <div className="title">LAST MONTH'S ENGINEER-MONTH ALLOCATION</div>
          {narrow ? (
            // phones: transposed — 3 discipline columns fit where 9 category columns can't
            <table>
              <thead>
                <tr>
                  <th></th>
                  {DISCIPLINES.map((d) => (
                    <th className="r" key={d}>
                      {d.slice(0, 6)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(
                  [
                    ["budget", (a) => a.budget],
                    ["nre", (a) => a.nre],
                    ["sustaining", (a) => a.sustaining],
                    ["node qual", (a) => a.qual],
                    ["yield res.", (a) => a.research],
                    ["reliability", (a) => a.reliability],
                    ["tools", (a) => a.tools],
                    ["projects", (a) => Object.values(a.byProject).reduce((x: number, y: number) => x + y, 0)],
                    ["idle", (a) => a.idle],
                  ] as [string, (a: TickReport["allocation"][Discipline]) => number][]
                ).map(([label, get]) => (
                  <tr key={label}>
                    <td>{label}</td>
                    {DISCIPLINES.map((d) => (
                      <td className="r" key={d}>
                        {get(snap.report!.allocation[d]).toFixed(1)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="scroll-x">
              <table>
                <thead>
                  <tr>
                    <th>discipline</th>
                    <th className="r">budget</th>
                    <th className="r">nre</th>
                    <th className="r">sustaining</th>
                    <th className="r">node qual</th>
                    <th className="r">yield res.</th>
                    <th className="r">reliability</th>
                    <th className="r">tools</th>
                    <th className="r">projects</th>
                    <th className="r">idle</th>
                  </tr>
                </thead>
                <tbody>
                  {DISCIPLINES.map((d) => {
                    const a = snap.report!.allocation[d];
                    const proj = Object.values(a.byProject).reduce((x, y) => x + y, 0);
                    return (
                      <tr key={d}>
                        <td>{d}</td>
                        <td className="r">{a.budget.toFixed(1)}</td>
                        <td className="r">{a.nre.toFixed(1)}</td>
                        <td className="r">{a.sustaining.toFixed(1)}</td>
                        <td className="r">{a.qual.toFixed(1)}</td>
                        <td className="r">{a.research.toFixed(1)}</td>
                        <td className="r">{a.reliability.toFixed(1)}</td>
                        <td className="r">{a.tools.toFixed(1)}</td>
                        <td className="r">{proj.toFixed(1)}</td>
                        <td className="r">{a.idle.toFixed(1)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <span className="hint">Idle engineer-months are pure burn. Sustaining tax grows with every active product.</span>
        </div>
      )}
    </div>
  );
}
